/**
 * AC-ADM-1: User Management (REAL)
 *
 * GraphQL operations:
 *   Query:    AcListUsers → listUsers[] { id email displayName status role personas }
 *             AcCurrentUser → currentUser (fallback if listUsers doesn't exist)
 *   Mutations: AcCreateUser, AcSuspendUser, AcCreateUsers (CSV bulk — ACT-1293)
 *
 * Fallback strategy: if listUsers query returns a GQL error (field not yet
 * on schema), the page falls back gracefully to showing the currentUser row only.
 * The Create User action uses createUser mutation regardless.
 */
import { useState, useMemo, useEffect, type ReactNode } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import {
  UserPlus,
  Ban,
  RotateCcw,
  RefreshCw,
  FileUp,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Pencil,
} from "lucide-react";

import LoadingSpinner from "../../components/LoadingSpinner";
import { AdminWall, isAuthWallError } from "../components/AdminWall";
import CsvUploadModal from "../components/CsvUploadModal";
import SearchableSelect, { type SelectOption } from "../components/SearchableSelect";
import { isHiddenPersona } from "../../config/roles";
import { useCurrentUser } from "../../auth/CurrentUserContext";
import {
  LIST_USERS_QUERY,
  CURRENT_USER_QUERY,
  CREATE_USER_MUTATION,
  SUSPEND_USER_MUTATION,
  UPDATE_USER_MUTATION,
  ASSIGN_PERSONAS_MUTATION,
  ASSIGN_ROLE_MUTATION,
  LIST_PERSONAS_QUERY,
  LIST_ROLES_QUERY,
  type ListUsersResult,
  type ListUsersInput,
  type CurrentUserResult,
  type AcUserSummary,
  type CreateUserVars,
  type SuspendUserVars,
  type UpdateUserVars,
  type AssignPersonasVars,
  type AssignRoleVars,
  type ListPersonasResult,
  type ListRolesResult,
} from "../graphql/adminOps";

// ---- Create User form types ----
interface CreateUserFormValues {
  email: string;
  displayName: string;
  roleCodes: string;
  personaCodes: string;
}

// Rows-per-page options for the pager (server-side: drives listUsers.input.pageSize).
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 10;

// Fixed status catalog for the Status filter — statuses are a closed set, not
// derived from the currently-loaded page (which would hide options once the
// filter/search narrows the result set).
const STATUS_OPTIONS = ["active", "suspended", "pending"] as const;

// Debounce delay (ms) before a search keystroke drives a new server request.
const SEARCH_DEBOUNCE_MS = 300;

// ---- Status label formatting ----
// The backend stores status as a lowercase string ("active" / "suspended" /
// "pending" — see care-management-gql AcAdminRepository), but that's a wire
// value, not a display value. Route every place a status renders as text
// through this single helper instead of trusting the raw casing — that's what
// produced the inconsistent pill (a lowercase "active" next to a
// Capitalized "Suspended") and the mismatched Status filter (a
// CSS-`capitalize` class on the closed <select> box, which most browsers
// don't apply to the native option-list popup, so the open dropdown showed
// lowercase while the closed box looked Capitalized).
export function formatStatusLabel(status: string | null | undefined): string {
  const s = status?.trim();
  if (!s) return "Active";
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

// ---- Status badge ----
function StatusBadge({ status }: { status: string | null }) {
  const s = status?.toLowerCase() ?? "active";
  const colorMap: Record<string, string> = {
    active: "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-300 dark:border-green-700",
    suspended: "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-300 dark:border-red-700",
    pending: "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-300 dark:border-yellow-700",
  };
  return (
    <span
      className={
        "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold " +
        (colorMap[s] ?? colorMap["active"])
      }
    >
      {formatStatusLabel(status)}
    </span>
  );
}

// ---- Filter select ----
// Native <select> (keeps full keyboard a11y and scales to many options) with
// appearance-none so the OS doesn't paint its own dropdown arrow on top of our
// chevron — that double-arrow + the default control height were what made the
// filters look "slightly off" and overlap when opened. h-9 matches the search
// input height exactly so the toolbar row aligns. In the narrow side panel the
// two filters flex to split the row evenly.
//
// Deliberately no CSS `capitalize` text-transform here: most browsers don't
// apply text-transform to the native <option> popup (only the closed select
// box inherits it reliably), which is what made the Status filter's open
// dropdown show lowercase values while the closed box looked Capitalized —
// a real, visible discrepancy. Pass already-Capitalized label text instead
// (see `formatStatusLabel`) so the closed box and the open list always match.
function FilterSelect({
  value,
  onChange,
  ariaLabel,
  children,
}: {
  value: string;
  onChange: (v: string) => void;
  ariaLabel: string;
  children: ReactNode;
}) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel}
        className={
          "h-9 w-full appearance-none rounded-lg border border-input bg-background " +
          "pl-3 pr-8 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring " +
          "cursor-pointer truncate"
        }
      >
        {children}
      </select>
      <ChevronDown
        className="w-4 h-4 absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
        aria-hidden="true"
      />
    </div>
  );
}

// ---- Create User Modal ----
function CreateUserModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const { register, handleSubmit, formState: { errors } } = useForm<CreateUserFormValues>({
    defaultValues: { email: "", displayName: "", roleCodes: "", personaCodes: "" },
  });

  const [createUser, { loading, error }] = useMutation<unknown, CreateUserVars>(
    CREATE_USER_MUTATION,
    { onCompleted: () => { onCreated(); onClose(); } },
  );

  const onSubmit = (vals: CreateUserFormValues) => {
    const roleCodes = vals.roleCodes.split(",").map((s) => s.trim()).filter(Boolean);
    const personaCodes = vals.personaCodes.split(",").map((s) => s.trim()).filter(Boolean);
    void createUser({
      variables: {
        email: vals.email,
        displayName: vals.displayName || undefined,
        roleCodes: roleCodes.length ? roleCodes : undefined,
        personaCodes: personaCodes.length ? personaCodes : undefined,
      },
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Create new user"
    >
      <div className="bg-card border border-border rounded-2xl shadow-xl w-full max-w-md mx-4 p-6">
        <h2 className="text-lg font-bold mb-4">Create New User</h2>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="email">
              Email <span className="text-destructive">*</span>
            </label>
            <input
              id="email"
              type="email"
              {...register("email", { required: "Email is required" })}
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="user@example.com"
            />
            {errors.email && (
              <p className="text-xs text-destructive mt-1">{errors.email.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="displayName">
              Display Name
            </label>
            <input
              id="displayName"
              type="text"
              {...register("displayName")}
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="Jane Smith"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="roleCodes">
              Role Codes <span className="text-muted-foreground text-xs">(comma-separated)</span>
            </label>
            <input
              id="roleCodes"
              type="text"
              {...register("roleCodes")}
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="admin"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="personaCodes">
              Persona Codes <span className="text-muted-foreground text-xs">(comma-separated)</span>
            </label>
            <input
              id="personaCodes"
              type="text"
              {...register("personaCodes")}
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="CARE_MANAGER"
            />
          </div>

          {error && (
            <p className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">
              {error.message}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50 hover:bg-primary/90 transition-colors"
            >
              {loading ? "Creating..." : "Create User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---- Inline persona multiselect (users-table cell) ----
// Replaces the old "Assign Personas to User" modal: the admin edits a user's personas
// directly from the row via a searchable multiselect that matches the persona-pill UI.
// Read-only pills until the admin clicks Edit, so the table stays scannable and the
// mutation only fires on an explicit Save.
function PersonaCell({
  user,
  personaOptions,
  onSaved,
}: {
  user: AcUserSummary;
  personaOptions: SelectOption[];
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string[]>(() => user.personas.map((p) => p.code));

  const [assignPersonas, { loading, error }] = useMutation<unknown, AssignPersonasVars>(
    ASSIGN_PERSONAS_MUTATION,
    {
      onCompleted: () => {
        setEditing(false);
        onSaved();
      },
    },
  );

  const startEditing = () => {
    setDraft(user.personas.map((p) => p.code));
    setEditing(true);
  };

  const save = () => {
    // assignPersonas replaces the whole set and the BE rejects an empty list (it would
    // wipe assignments). The Save button is disabled when the draft is empty, so this is
    // just a defensive guard — never silently close on empty.
    if (draft.length === 0) return;
    void assignPersonas({ variables: { userId: user.id, personaCodes: draft } });
  };

  if (!editing) {
    return (
      <div className="flex flex-wrap items-center gap-1">
        {user.personas.length > 0 ? (
          user.personas.map((p) => (
            <span
              key={p.code}
              className="inline-flex items-center rounded-full bg-secondary border border-border px-2 py-0.5 text-xs"
            >
              {p.displayName}
            </span>
          ))
        ) : (
          <span className="text-muted-foreground text-xs">—</span>
        )}
        <button
          type="button"
          onClick={startEditing}
          aria-label={`Edit personas for ${user.email}`}
          className="ml-1 p-1 rounded text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          title="Edit personas"
        >
          <Pencil className="w-3 h-3" aria-hidden="true" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5 min-w-[220px]">
      <SearchableSelect
        multiple
        compact
        value={draft}
        onChange={setDraft}
        options={personaOptions}
        ariaLabel={`Personas for ${user.email}`}
        placeholder="Add persona…"
      />
      {error && (
        <p className="text-xs text-destructive">{error.message}</p>
      )}
      {draft.length === 0 && (
        <p className="text-xs text-muted-foreground">
          At least one persona is required.
        </p>
      )}
      <div className="flex gap-1.5">
        <button
          type="button"
          onClick={save}
          disabled={loading || draft.length === 0}
          className="px-2 py-1 rounded-md bg-primary text-primary-foreground text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90 transition-colors"
        >
          {loading ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          onClick={() => setEditing(false)}
          disabled={loading}
          className="px-2 py-1 rounded-md border border-border text-xs font-medium hover:bg-accent transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ---- Suspend / Reactivate toggle (users-table Actions cell) ----
// ONE deliberate two-state button, keyed strictly off whether the user is
// currently suspended — not an ad-hoc `disabled:opacity-40` dimming of a
// single "Suspend" button (which is what made the action look like it was
// rendering two different, conflicting styles: a crisp destructive-outline
// button on active rows vs. a washed-out half-opacity version of the same
// button, still labeled "Suspend", on already-suspended rows). Same shape,
// size, and icon size in both states; only color/icon/label/handler flip.
export function SuspendToggleButton({
  user,
  suspending,
  reactivating,
  onSuspend,
  onReactivate,
}: {
  user: AcUserSummary;
  suspending: boolean;
  reactivating: boolean;
  onSuspend: () => void;
  onReactivate: () => void;
}) {
  const isSuspended = user.status?.toLowerCase() === "suspended";

  if (isSuspended) {
    return (
      <button
        type="button"
        disabled={reactivating}
        onClick={() => {
          if (window.confirm(`Reactivate user ${user.email}?`)) onReactivate();
        }}
        title="Reactivate user"
        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-primary/40 text-primary hover:bg-primary/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" />
        Reactivate
      </button>
    );
  }

  return (
    <button
      type="button"
      disabled={suspending}
      onClick={() => {
        if (window.confirm(`Suspend user ${user.email}?`)) onSuspend();
      }}
      title="Suspend user"
      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-destructive/40 text-destructive hover:bg-destructive/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
    >
      <Ban className="w-3.5 h-3.5" aria-hidden="true" />
      Suspend
    </button>
  );
}

// ---- Inline role editor (users-table cell) ----
// Lets an admin change an existing user's role directly from the row, mirroring the
// PersonaCell UX: a read-only pill until the admin clicks Edit, then a single-select
// (the user's one effective/highest-precedence role) with an explicit Save. Gated on
// `canEdit` (useCurrentUser().isAdmin) so the edit affordance only appears for admins —
// defense-in-depth on top of the page's AdminWall and the BE's assertAdmin gate.
// Exported for unit testing (the admin-guard + edit-reveal behavior).
export function RoleCell({
  user,
  roleOptions,
  canEdit,
  onSaved,
}: {
  user: AcUserSummary;
  roleOptions: SelectOption[];
  canEdit: boolean;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string | null>(user.role?.code ?? null);

  const [assignRole, { loading, error }] = useMutation<unknown, AssignRoleVars>(
    ASSIGN_ROLE_MUTATION,
    {
      onCompleted: () => {
        setEditing(false);
        onSaved();
      },
    },
  );

  const startEditing = () => {
    setDraft(user.role?.code ?? null);
    setEditing(true);
  };

  const currentCode = user.role?.code ?? null;

  const save = () => {
    // assignRole replaces the user's role with a single code; the BE rejects an
    // unknown/empty code. Save is disabled with nothing selected, so this is a
    // defensive guard — never fire an empty/unchanged mutation.
    if (!draft || draft === currentCode) return;
    void assignRole({ variables: { userId: user.id, roleCode: draft } });
  };

  const rolePill = user.role ? (
    <span className="inline-flex items-center rounded-full bg-primary/10 border border-primary/20 px-2 py-0.5 text-xs font-medium text-primary">
      {user.role.displayName}
    </span>
  ) : (
    <span className="text-muted-foreground text-xs">—</span>
  );

  if (!editing) {
    return (
      <div className="flex items-center gap-1">
        {rolePill}
        {canEdit && (
          <button
            type="button"
            onClick={startEditing}
            aria-label={`Edit role for ${user.email}`}
            className="ml-1 p-1 rounded text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            title="Edit role"
          >
            <Pencil className="w-3 h-3" aria-hidden="true" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5 min-w-[180px]">
      <SearchableSelect
        compact
        value={draft}
        onChange={setDraft}
        options={roleOptions}
        ariaLabel={`Role for ${user.email}`}
        placeholder="Select role…"
      />
      {error && <p className="text-xs text-destructive">{error.message}</p>}
      <div className="flex gap-1.5">
        <button
          type="button"
          onClick={save}
          disabled={loading || !draft || draft === currentCode}
          className="px-2 py-1 rounded-md bg-primary text-primary-foreground text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90 transition-colors"
        >
          {loading ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          onClick={() => setEditing(false)}
          disabled={loading}
          className="px-2 py-1 rounded-md border border-border text-xs font-medium hover:bg-accent transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ---- Main Page ----
export default function UsersPage() {
  const { isAdmin } = useCurrentUser();
  const [showCreate, setShowCreate] = useState(false);
  const [showCsvImport, setShowCsvImport] = useState(false);
  // Search / filter / pagination state. Every filter/search/pageSize change
  // resets page to 1 so the user never lands on an out-of-range page.
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [personaFilter, setPersonaFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  // Debounce the free-text search ~300ms before it drives a new server
  // request, then reset to page 1 once the debounced value actually changes.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  // Primary: server-side paginated/filtered/searched user list (AcUsersPage).
  // cache-and-network: always revalidates against the BE (search/filter/page
  // combinations are numerous — a stale cache-first read would silently show
  // wrong results) while still painting instantly from cache when warm.
  // errorPolicy:"all" keeps partial data usable and falls through to the
  // currentUser-only view below when the field errors (e.g. not on schema yet).
  const {
    data: listData,
    previousData: listPrevData,
    loading: listLoading,
    error: listError,
    refetch: refetchList,
  } = useQuery<ListUsersResult, { input: ListUsersInput }>(LIST_USERS_QUERY, {
    variables: {
      input: {
        page: page - 1,
        pageSize,
        search: debouncedSearch || undefined,
        status: statusFilter === "all" ? undefined : statusFilter,
        roleCode: roleFilter === "all" ? undefined : roleFilter,
        personaCode: personaFilter === "all" ? undefined : personaFilter,
      },
    },
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
    // Flip `loading` on every variable change (page / search / filter / pageSize)
    // so refetches surface the table loading overlay, not just the first load.
    notifyOnNetworkStatusChange: true,
  });

  // Fallback: current user (always available).
  // cache-first for the same reason — this query is already fetched by
  // CurrentUserProvider above; reading from cache is always instant.
  const { data: currentData, refetch: refetchCurrent } = useQuery<CurrentUserResult>(
    CURRENT_USER_QUERY,
    { fetchPolicy: "cache-first" },
  );

  // Persona catalog for the inline multiselect AND the Personas filter.
  // cache-first (PersonasPage warms it); errorPolicy:"all" so a schema-miss
  // degrades to an empty option list rather than blocking the users table.
  const { data: personasData } = useQuery<ListPersonasResult>(LIST_PERSONAS_QUERY, {
    fetchPolicy: "cache-first",
    errorPolicy: "all",
  });

  // Role catalog for the Role filter. cache-first; errorPolicy:"all" so a
  // schema-miss degrades to an empty option list rather than blocking the page.
  const { data: rolesData } = useQuery<ListRolesResult>(LIST_ROLES_QUERY, {
    fetchPolicy: "cache-first",
    errorPolicy: "all",
  });

  // Hide vestigial personas (ADMIN) from the assign-persona multiselect — admin
  // access is role-driven now, so offering the ADMIN persona would be misleading.
  const personaOptions: SelectOption[] = useMemo(
    () =>
      (personasData?.listPersonas ?? [])
        .filter((p) => !isHiddenPersona(p.code))
        .map((p) => ({
          value: p.code,
          label: p.displayName,
        })),
    [personasData],
  );

  // Full catalogs for the filter dropdowns — deliberately NOT derived from the
  // currently-loaded page, so narrowing search/filters never hides an option.
  const roleOptions = useMemo(
    () => (rolesData?.listRoles ?? []).map((r) => ({ code: r.code, label: r.displayName })),
    [rolesData],
  );

  // Same role catalog shaped for the inline RoleCell single-select editor.
  const roleSelectOptions: SelectOption[] = useMemo(
    () => (rolesData?.listRoles ?? []).map((r) => ({ value: r.code, label: r.displayName })),
    [rolesData],
  );

  const personaFilterOptions = useMemo(
    () =>
      (personasData?.listPersonas ?? [])
        .filter((p) => !isHiddenPersona(p.code))
        .map((p) => ({ code: p.code, label: p.displayName })),
    [personasData],
  );

  const [suspendUser, { loading: suspending }] = useMutation<unknown, SuspendUserVars>(
    SUSPEND_USER_MUTATION,
    {
      onCompleted: () => {
        void refetchList();
        void refetchCurrent();
      },
      onError: (e) => toast.error(`Failed to suspend user: ${e.message}`),
    },
  );

  // Reactivate is the suspend toggle's other half — updateUser already supports
  // an arbitrary status, so no new BE mutation is needed. Wiring this in (it
  // previously wasn't used anywhere on this page) is what lets the Actions
  // column render ONE deliberate two-state button instead of a single
  // "Suspend" button that just went semi-transparent once a user was already
  // suspended.
  const [reactivateUser, { loading: reactivating }] = useMutation<unknown, UpdateUserVars>(
    UPDATE_USER_MUTATION,
    {
      onCompleted: () => {
        void refetchList();
        void refetchCurrent();
      },
      onError: (e) => toast.error(`Failed to reactivate user: ${e.message}`),
    },
  );

  const handleCreated = () => {
    void refetchList();
    void refetchCurrent();
  };

  // Build the user rows to display.
  // Prefer current data, fall back to previousData so the table stays
  // populated during a background refetch rather than flashing a spinner.
  const effectiveListData = listData ?? listPrevData;
  let users: AcUserSummary[];
  let totalRecords: number;
  const listFailed = !!listError && !effectiveListData?.listUsers;

  if (effectiveListData?.listUsers) {
    // Already the server-filtered + paged page — render it directly, no
    // client-side filtering/slicing.
    users = effectiveListData.listUsers.items;
    totalRecords = effectiveListData.listUsers.pageInformation?.totalRecords ?? users.length;
  } else if (currentData?.currentUser) {
    // Fallback: listUsers errored (e.g. not yet on schema) — show only the
    // current user; pager/count degrade gracefully since there's no BE total.
    const u = currentData.currentUser;
    users = [{
      id: u.id,
      email: u.email,
      displayName: u.displayName,
      status: "active",
      role: u.role,
      personas: u.personas.map((p) => ({ code: p.code, displayName: p.displayName })),
    }];
    totalRecords = users.length;
  } else {
    users = [];
    totalRecords = 0;
  }

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  const safePage = Math.min(page, totalPages);

  return (
    <div>
      <AdminWall error={listError} screenLabel="Users">
      <div className="flex justify-between mb-4 gap-3 items-center">
        <div>
          <h1 className="text-xl font-bold">Users</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Manage Atlas Copilot users, roles, and personas
          </p>
          {listFailed && !isAuthWallError(listError) && (
            <p className="text-xs text-amber-700 dark:text-amber-400 mt-1 bg-amber-50 dark:bg-amber-900/20 rounded px-2 py-1">
              <strong>Note:</strong> listUsers query not yet on schema — showing current user only.
              The backend needs to expose a <code>listUsers</code> query for full user management.
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => { void refetchList(); void refetchCurrent(); }}
            className="p-2 rounded-lg border border-border hover:bg-accent transition-colors shrink-0"
            aria-label="Refresh"
          >
            <RefreshCw className="w-4 h-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => setShowCsvImport(true)}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition-colors shrink-0"
          >
            <FileUp className="w-4 h-4 shrink-0" aria-hidden="true" />
            Import CSV
          </button>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors shrink-0"
          >
            <UserPlus className="w-4 h-4 shrink-0" aria-hidden="true" />
            New User
          </button>
        </div>
      </div>

      {/* Search + filter toolbar. Scales the table to thousands of users:
          filter narrows the set, the pager bounds the rendered rows. */}
      <div className="flex gap-2 mb-3 flex-wrap items-center">
        <div className="relative flex-1 min-w-0">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, or persona…"
            aria-label="Search users"
            className="h-9 w-full rounded-lg border border-input bg-background pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <div className="flex gap-2 shrink-0">
          <FilterSelect
            value={roleFilter}
            onChange={(v) => { setRoleFilter(v); setPage(1); }}
            ariaLabel="Filter by role"
          >
            <option value="all">All roles</option>
            {roleOptions.map((r) => (
              <option key={r.code} value={r.code}>{r.label}</option>
            ))}
          </FilterSelect>
          <FilterSelect
            value={statusFilter}
            onChange={(v) => { setStatusFilter(v); setPage(1); }}
            ariaLabel="Filter by status"
          >
            <option value="all">All statuses</option>
            {STATUS_OPTIONS.map((s) => (
              // value stays the lowercase wire value (backend does an exact-match
              // `WHERE status = $1`, no LOWER()) — only the label is formatted.
              <option key={s} value={s}>{formatStatusLabel(s)}</option>
            ))}
          </FilterSelect>
          <FilterSelect
            value={personaFilter}
            onChange={(v) => { setPersonaFilter(v); setPage(1); }}
            ariaLabel="Filter by persona"
          >
            <option value="all">All personas</option>
            {personaFilterOptions.map((p) => (
              <option key={p.code} value={p.code}>{p.label}</option>
            ))}
          </FilterSelect>
        </div>
      </div>

      {listLoading && users.length === 0 ? (
        <LoadingSpinner fullScreen={false} label="Loading users..." />
      ) : (
        <div className="relative rounded-xl border border-border bg-card overflow-x-auto">
          {listLoading && (
            <div
              className="absolute inset-0 z-10 flex items-center justify-center bg-background/60 backdrop-blur-[1px]"
              role="status"
              aria-live="polite"
              aria-label="Loading users"
            >
              <LoadingSpinner fullScreen={false} label="Loading…" />
            </div>
          )}
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="text-left px-4 py-3 font-semibold text-muted-foreground">User</th>
                <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Role</th>
                <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Personas</th>
                <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Status</th>
                <th className="text-right px-4 py-3 font-semibold text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                    No users found.
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr
                    key={user.id}
                    className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <p className="font-medium">{user.displayName ?? "—"}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <RoleCell
                        user={user}
                        roleOptions={roleSelectOptions}
                        canEdit={isAdmin}
                        onSaved={() => { void refetchList(); void refetchCurrent(); }}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <PersonaCell
                        user={user}
                        personaOptions={personaOptions}
                        onSaved={() => { void refetchList(); void refetchCurrent(); }}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={user.status} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <SuspendToggleButton
                        user={user}
                        suspending={suspending}
                        reactivating={reactivating}
                        onSuspend={() => void suspendUser({ variables: { userId: user.id } })}
                        onReactivate={() =>
                          void reactivateUser({ variables: { userId: user.id, status: "active" } })
                        }
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Result count + pager */}
      {totalRecords > 0 && (
        <div className="flex items-center gap-3 mt-3 text-sm text-muted-foreground justify-between">
          <span>
            {totalRecords} user{totalRecords === 1 ? "" : "s"}
          </span>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 shrink-0">
              <label htmlFor="page-size" className="whitespace-nowrap">
                Rows per page
              </label>
              <div className="relative">
                <select
                  id="page-size"
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                  aria-label="Rows per page"
                  className="h-8 appearance-none rounded-lg border border-input bg-background pl-2.5 pr-7 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring cursor-pointer"
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
                <ChevronDown
                  className="w-3.5 h-3.5 absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
                  aria-hidden="true"
                />
              </div>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  aria-label="Previous page"
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-border hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" aria-hidden="true" />
                </button>
                <span className="tabular-nums">
                  Page {safePage} of {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                  aria-label="Next page"
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-border hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4" aria-hidden="true" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {showCreate && (
        <CreateUserModal onClose={() => setShowCreate(false)} onCreated={handleCreated} />
      )}
      {showCsvImport && (
        <CsvUploadModal
          onClose={() => setShowCsvImport(false)}
          onImported={handleCreated}
        />
      )}
      </AdminWall>
    </div>
  );
}
